import { Button, Notification, Spinner, toast } from 'components/ui'
import React, { useContext, useState } from 'react'
import ParticleBackground from 'react-particle-backgrounds'
import { particleBackgroundSettingsButton } from 'configs/particle-background.config';
import { BsStars } from 'react-icons/bs';
import { TbCircleCheckFilled } from 'react-icons/tb';
import { HiOutlineSparkles, HiRocketLaunch } from 'react-icons/hi2';
import { Trans } from 'react-i18next';
import { SessionContext } from 'contexts/SessionContext';
import { ModalContext } from 'contexts/ModalContext';
import { PriorityModalContext } from 'contexts/PriorityModalContext';
import { useWhitelabel } from 'contexts/WhitelabelThemeContext';
import { api as Api } from 'api/http';

export default function EndTrialModal() {
  
  const { updateUserData, user } = useContext(SessionContext);
  const { closeModalPriority } = useContext(PriorityModalContext);
  const { isWhitelabel, whitelabel } = useWhitelabel();

  const [isEndingTrial, setIsEndingTrial] = useState(false);

  const handleEndTrial = async () => {
    setIsEndingTrial(true);
    try {
      // Update subscription to end trial immediately
      await Api.put(`/subscriptions`, {}, { 
        end_trial: true 
      });
      await updateUserData();
      toast.push(<Notification title="Trial ended successfully. You now have full plan capabilities!" type="success" />);
      closeModalPriority();
    } catch (err) {
      toast.push(<Notification title={err?.message || "Failed to end trial. Please try again."} type="danger" />);
    } finally {
      setIsEndingTrial(false);
    }
  };

  const handleSkip = () => {
    closeModalPriority();
  };

  return (
    <div className='relative'>
      <div className='bg-gradient-primary rounded-2xl overflow-hidden relative max-h-[90svh] overflow-y-auto'>
        <ParticleBackground settings={particleBackgroundSettingsButton} className="absolute pointer-events-none" />
        <div className='w-full h-full flex flex-col gap-6 lg:gap-8 items-center justify-center p-4 lg:p-8 py-8 lg:py-12 relative z-[1]'>
          <div className="flex items-center gap-3">
            <HiOutlineSparkles className='text-4xl text-secondary-400' />
            <h2 className='mega-title text-white flex items-center gap-2'>
              {isWhitelabel ? (
                <i className='font-bold text-white text-4xl'>{whitelabel?.name}</i>
              ) : (
                <img 
                  src="/img/logo/logo-dark-full.png" 
                  className='h-8'
                />
              )}
            </h2>
            <HiOutlineSparkles className='text-4xl text-secondary-400' />
          </div>
          
          <div className="text-center">
            <h3 className='text-white text-center text-xl lg:text-3xl font-black mb-2'>
              Ready to unlock your full potential?
            </h3>
            <p className='text-tertiary-300 text-center text-sm font-bold max-w-md'>
              You've reached your trial limits! End your free trial now to unlock unlimited access to all features.
            </p>
          </div>

          <div className='grid lg:grid-cols-2 gap-3 mt-4'>
            <div className='flex items-center gap-2 px-4 relative'>
              <div className='relative'>
                <span className='block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-secondary-500 rounded-full' />
                <TbCircleCheckFilled 
                  className='text-2xl text-green-500 relative z-[1]'
                />
              </div>
              <span className='text-white text-sm font-bold'>Unlimited AI generations</span>
            </div>
            <div className='flex items-center gap-2 px-4 relative'>
              <div className='relative'>
                <span className='block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-secondary-500 rounded-full' />
                <TbCircleCheckFilled 
                  className='text-2xl text-green-500 relative z-[1]'
                />
              </div>
              <span className='text-white text-sm font-bold'>Complete feature unlock</span>
            </div>
            <div className='flex items-center gap-2 px-4 relative'>
              <div className='relative'>
                <span className='block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-secondary-500 rounded-full' />
                <TbCircleCheckFilled 
                  className='text-2xl text-green-500 relative z-[1]'
                />
              </div>
              <span className='text-white text-sm font-bold'>Advanced AI models</span>
            </div>
            <div className='flex items-center gap-2 px-4 relative'>
              <div className='relative'>
                <span className='block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-secondary-500 rounded-full' />
                <TbCircleCheckFilled 
                  className='text-2xl text-green-500 relative z-[1]'
                />
              </div>
              <span className='text-white text-sm font-bold'>Maximum content quality</span>
            </div>
          </div>

          <div className="bg-white/10 px-6 py-4 rounded-xl text-center">
            <p className='text-white text-lg font-bold mb-1'>
              <Trans i18nKey="endTrialModal.unlockNow">Unlock everything now!</Trans>
            </p>
            <p className='text-tertiary-300 text-sm'>
              <Trans i18nKey="endTrialModal.trialRestrictions">Remove all trial restrictions instantly</Trans>
            </p>
          </div>

          <div className='flex flex-col sm:flex-row justify-center gap-3 mt-4 w-full max-w-md'>
            <Button 
              variant="ai"
              icon={<HiRocketLaunch />}
              onClick={handleEndTrial}
              loading={isEndingTrial}
              className="flex-1"
            >
              <Trans i18nKey="endTrialModal.endTrial">End Trial Now</Trans>
            </Button>
            <Button 
              variant="plain"
              onClick={handleSkip}
              disabled={isEndingTrial}
              className="flex-1 text-white border-white/30 hover:bg-white/10"
            >
              <Trans i18nKey="endTrialModal.continueTrial">Continue Trial</Trans>
            </Button>
          </div>

          <p className='text-tertiary-400 text-xs text-center max-w-sm'>
            <Trans i18nKey="endTrialModal.disclaimer">
              By ending your trial, you'll immediately unlock all features and remove all usage limits.
            </Trans>
          </p>
        </div>
      </div>
    </div>
  );
} 